import { Component, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormField, form, max, maxLength } from '@angular/forms/signals';
import { ISucursalData } from '@core/interfaces/entidades/sucursal.entidad';
import { SucursalApiService } from '@core/services/api/sucursal-api.service';
import { FormErrorMessagePipe } from '@shared/pipes/form-error-message.pipe';
import { AppService } from '@shared/services/app.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { ToastService } from '@shared/services/toast.service';
import { buildDiff, normalizarTelefono } from '@shared/utils/functions.utils';
import { UiInputMask } from '@shared/components/ui';

@Component({
  selector: 'app-config-sucursal',
  imports: [FormField, UiInputMask, FormsModule, FormErrorMessagePipe],
  templateUrl: './config-sucursal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './config-sucursal.css',
})
export class ConfigSucursal implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cargarConfigSucursal();
      }
    });
  }
  // servicios
  private appService = inject(AppService);
  private sucursalApiService = inject(SucursalApiService);
  private sucursalService = inject(SucursalService);
  private toastService = inject(ToastService);

  // datos
  numero_celular?: string;
  sucursal_actual = this.sucursalService.activeSucursal;
  sucursal_old = signal<ISucursalData | null>(null);
  formulario_sucursal = form(
    signal({
      nombre: '',
      direccion: '',
      email: '',
      activo: false,
      es_principal: false,
      serie_boleta: '',
      correlativo_boleta: 0,
      serie_factura: '',
      correlativo_factura: 0,
      serie_nota_credito: '',
      correlativo_nota_credito: 0,
      serie_nota_venta: '',
      correlativo_nota_venta: 0,
      footer_factura: '',
      footer_boleta: '',
      footer_nota_venta: '',
    }),
    (x) => {
      maxLength(x.nombre, 100, { message: 'El nombre no puede exceder los 100 caracteres.' });
      maxLength(x.direccion, 200, { message: 'La dirección no puede exceder los 200 caracteres.' });
      maxLength(x.email, 120, { message: 'El email no puede exceder los 120 caracteres.' });
      maxLength(x.serie_boleta, 10, {
        message: 'La serie de boleta no puede exceder los 10 caracteres.',
      });
      maxLength(x.serie_factura, 10, {
        message: 'La serie de factura no puede exceder los 10 caracteres.',
      });
      maxLength(x.serie_nota_credito, 10, {
        message: 'La serie de nota de crédito no puede exceder los 10 caracteres.',
      });
      maxLength(x.serie_nota_venta, 10, {
        message: 'La serie de nota de venta no puede exceder los 10 caracteres.',
      });
      max(x.correlativo_boleta, 99999999, {
        message: 'El correlativo de boleta no puede ser mayor a 99,999,999.',
      });
      max(x.correlativo_factura, 99999999, {
        message: 'El correlativo de factura no puede ser mayor a 99,999,999.',
      });
      max(x.correlativo_nota_credito, 99999999, {
        message: 'El correlativo de nota de crédito no puede ser mayor a 99,999,999.',
      });
      max(x.correlativo_nota_venta, 99999999, {
        message: 'El correlativo de nota de venta no puede ser mayor a 99,999,999.',
      });
    },
  );
  // metodos
  private cargarConfigSucursal() {
    if (!this.sucursal_actual()) {
      return;
    }
    this.sucursalApiService.cargar(this.sucursal_actual()!.id).subscribe({
      next: (sucursal) => {
        this.sucursal_old.set(sucursal);
        this.formulario_sucursal().value.set({
          nombre: sucursal.nombre,
          direccion: sucursal.direccion || '',
          email: sucursal.email || '',
          activo: sucursal.activo,
          es_principal: sucursal.es_principal,
          serie_boleta: sucursal.serie_boleta,
          correlativo_boleta: sucursal.correlativo_boleta,
          serie_factura: sucursal.serie_factura,
          correlativo_factura: sucursal.correlativo_factura,
          serie_nota_credito: sucursal.serie_nota_credito,
          correlativo_nota_credito: sucursal.correlativo_nota_credito,
          serie_nota_venta: sucursal.serie_nota_venta,
          correlativo_nota_venta: sucursal.correlativo_nota_venta,
          footer_factura: sucursal.footer_factura || '',
          footer_boleta: sucursal.footer_boleta || '',
          footer_nota_venta: sucursal.footer_nota_venta || '',
        });
        this.numero_celular = sucursal.telefono || '';
      },
      error: (err) => {
        console.error('Error al cargar la sucursal', err);
      },
    });
  }
  private checkInvalidForm() {
    if (this.formulario_sucursal().invalid()) {
      this.formulario_sucursal.nombre().markAsTouched();
      this.formulario_sucursal.direccion().markAsTouched();
      this.formulario_sucursal.email().markAsTouched();
      this.formulario_sucursal.serie_boleta().markAsTouched();
      this.formulario_sucursal.correlativo_boleta().markAsTouched();
      this.formulario_sucursal.serie_factura().markAsTouched();
      this.formulario_sucursal.correlativo_factura().markAsTouched();
      this.formulario_sucursal.serie_nota_credito().markAsTouched();
      this.formulario_sucursal.correlativo_nota_credito().markAsTouched();
      this.formulario_sucursal.serie_nota_venta().markAsTouched();
      this.formulario_sucursal.correlativo_nota_venta().markAsTouched();
      return true;
    }
    return false;
  }
  guardarSucursal() {
    if (this.checkInvalidForm() || !this.sucursal_old()) return;
    const {
      nombre,
      direccion,
      email,
      activo,
      es_principal,
      serie_boleta,
      correlativo_boleta,
      serie_factura,
      correlativo_factura,
      serie_nota_credito,
      correlativo_nota_credito,
      serie_nota_venta,
      correlativo_nota_venta,
      footer_factura,
      footer_boleta,
      footer_nota_venta,
    } = this.formulario_sucursal().controlValue();

    const data: Partial<ISucursalData> = buildDiff(
      {
        ...this.sucursal_old(),
        telefono: normalizarTelefono(this.sucursal_old()?.telefono || ''),
      },
      {
        nombre,
        direccion,
        email,
        activo,
        es_principal,
        serie_boleta,
        correlativo_boleta,
        serie_factura,
        correlativo_factura,
        serie_nota_credito,
        correlativo_nota_credito,
        serie_nota_venta,
        correlativo_nota_venta,
        telefono: normalizarTelefono(this.numero_celular || ''),
        footer_factura: footer_factura?.trim() || undefined,
        footer_boleta: footer_boleta?.trim() || undefined,
        footer_nota_venta: footer_nota_venta?.trim() || undefined,
      },
    );
    if (Object.keys(data).length === 0) {
      this.toastService.show(
        'top-right',
        'info',
        'Sin cambios',
        'No se han realizado cambios en la configuración de la sucursal.',
      );
      return;
    }
    this.sucursalApiService.actualizar(this.sucursal_actual()!.id, data).subscribe({
      next: (sucursalActualizada) => {
        this.sucursalService.syncSucursalActive(sucursalActualizada);
        this.sucursal_old.set(sucursalActualizada);
        this.toastService.show(
          'top-right',
          'success',
          'Sucursal Actualizada',
          'La configuración de la sucursal se ha actualizado correctamente.',
        );
      },
      error: (err) => {
        console.error('Error al actualizar la sucursal', err);
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          'Ocurrió un error al actualizar la configuración de la sucursal.',
        );
      },
    });
  }
  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Configuración de la sucursal',
      subtitle: 'Aquí puedes ver la información de la sucursal',
      showBackButton: true,
    });
  }
}
